<template>
  <div
    v-if="showBanner"
    class="cookie-banner"
    :class="{ visible: bannerVisible }"
    role="dialog"
    aria-labelledby="cookie-title"
  >
    <div class="cookie-banner-inner">
      <div class="cookie-text">
        <strong id="cookie-title">{{ t('cookies.title') }}</strong>
        <p>{{ t('cookies.text') }}</p>
      </div>
      <div class="cookie-actions">
        <button type="button" class="cookie-btn secondary" @click="choose('reject')">{{ t('cookies.reject') }}</button>
        <button type="button" class="cookie-btn primary" @click="choose('accept')">{{ t('cookies.accept') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useLocale } from '../composables/useLocale'

const { t } = useLocale()

const COOKIE_KEY = 'mr-cookie-choice'

const showBanner = ref(false)
const bannerVisible = ref(false)

function getGaId() {
  if (typeof window === 'undefined') return null
  const host = window.location.hostname
  if (host === 'www.minderrente.nl' || host === 'minderrente.nl') return 'G-7CK27947LD'
  if (host.includes('staging')) return 'G-RZELXDYJKR'
  return null
}

function gtag() {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(arguments)
}

function initAnalytics() {
  const gaId = getGaId()
  if (!gaId || window.__gaInitialized) return
  window.__gaInitialized = true

  gtag('consent', 'default', {
    analytics_storage: 'denied',
  })

  const s = document.createElement('script')
  s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
  s.async = true
  document.head.appendChild(s)

  gtag('js', new Date())
  gtag('config', gaId, { anonymize_ip: true })
}

function grantConsent() {
  gtag('consent', 'update', {
    analytics_storage: 'granted',
  })
}

function choose(choice) {
  try {
    localStorage.setItem(COOKIE_KEY, choice)
  } catch (e) {
    // localStorage geblokkeerd (private mode)
  }
  bannerVisible.value = false
  setTimeout(() => { showBanner.value = false }, 350)
  if (choice === 'accept') {
    grantConsent()
  }
}

onMounted(() => {
  initAnalytics()

  let choice = null
  try { choice = localStorage.getItem(COOKIE_KEY) } catch (e) { /* */ }

  if (choice === 'accept') {
    grantConsent()
    return
  }
  if (choice === 'reject') {
    return
  }

  setTimeout(() => {
    showBanner.value = true
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { bannerVisible.value = true })
    })
  }, 800)
})
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 90;
  background: var(--bg-card);
  border: 1px solid var(--line-soft);
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(14, 42, 78, 0.18);
  max-width: 880px;
  margin: 0 auto;
  transform: translateY(20px);
  opacity: 0;
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
              opacity 0.35s ease;
}
.cookie-banner.visible {
  transform: translateY(0);
  opacity: 1;
}
.cookie-banner-inner {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;
  align-items: center;
  padding: 20px 22px;
}
@media (max-width: 600px) {
  .cookie-banner-inner {
    grid-template-columns: 1fr;
    gap: 14px;
  }
}
.cookie-text strong {
  display: block;
  font-family: 'Fraunces', serif;
  font-size: 16px;
  font-weight: 800;
  color: var(--ink);
  margin-bottom: 4px;
}
.cookie-text p {
  font-size: 13px;
  color: var(--ink-soft);
  line-height: 1.5;
  margin: 0;
}
.cookie-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
@media (max-width: 600px) {
  .cookie-actions { width: 100%; }
  .cookie-actions .cookie-btn { flex: 1; }
}
.cookie-btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  padding: 10px 18px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, transform 0.2s;
}
.cookie-btn.primary {
  background: var(--blue);
  color: #fff;
}
.cookie-btn.primary:hover {
  background: var(--blue-dark);
  transform: translateY(-1px);
}
.cookie-btn.secondary {
  background: var(--bg-soft);
  color: var(--ink);
  border: 1px solid var(--line-soft);
}
.cookie-btn.secondary:hover {
  background: var(--bg-warm);
}
</style>
